import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ToastService } from 'mbs-ui-kit';
import { DeepSyncCodes, ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { MaxSymbolInBrief } from 'mbs-ui-shared@constants/misc-constants';
import { ErrorHandlerService } from 'mbs-ui-shared@services/error-handler.service';
import { forbiddenErrorText, invalidCredentialsText, unidentifiedErrorText } from '../error-handler.interceptor';

@Injectable({ providedIn: 'root' })
export class ThrowErrorWithHandlingHelper {
  private toastService = inject(ToastService);
  private errorService = inject(ErrorHandlerService);

  init(req: HttpRequest<any>, error: HttpErrorResponse): void {
    const errorResponse = error.error;

    // title required field
    if (errorResponse?.title) {
      this.handleTitleError(errorResponse, error);
    } else {
      this.handleAnyError(errorResponse, error);
    }
  }

  public isDeepSyncError(errorResponse: any): boolean {
    const code = +errorResponse.errorCode;
    return DeepSyncCodes.includes(code) || code === ErrorCode.InvalidDecryptionPassword;
  }

  public handleLicenseError() {
    this.errorService.showSubscriptionErrorToast();
  }

  private handleTitleError(errorResponse: any, error: HttpErrorResponse): void {
    if (errorResponse?.detail?.length > MaxSymbolInBrief) {
      const invalidCredentials =
        this.errorService.isRemoteCommand(error) &&
        errorResponse?.method === 'CheckVMSocketLicenses' &&
        errorResponse.detail?.includes('ESXi credential');
      const errorText = invalidCredentials ? invalidCredentialsText : errorResponse.ErrorMessage || errorResponse.detail;

      this.errorService.showToastWithDetails({ error: errorResponse, modalText: errorText }, error.url);
    } else {
      this.errorService.showErrorToast(errorResponse.detail || errorResponse.title, '', error.url);
    }
  }

  private handleAnyError(errorResponse: any, error: HttpErrorResponse): void {
    if (errorResponse && typeof errorResponse !== 'string') {
      if (this.isGetBackupContentError(errorResponse)) {
        this.toastService.info('No backups to display');
      } else if (this.isOdataError) {
        this.errorService.showErrorToast(errorResponse.value || errorResponse?.error?.message || unidentifiedErrorText, 'Error', error.url);
      } else if (this.isGetComputerContentError(errorResponse)) {
        this.errorService.showErrorToast(errorResponse.error || 'The disk is locked.', '', error.url);
      } else if (this.isRemoteCommandError(error)) {
        this.errorService.showErrorToast(errorResponse.message || unidentifiedErrorText, '', error.url);
      }
    } else {
      this.errorService.showErrorToast(error.status === 403 ? forbiddenErrorText : unidentifiedErrorText, '', error.url);
    }
  }

  private isOdataError(errorResponse: any): boolean {
    return errorResponse?.value && errorResponse?.['@odata.context'];
  }

  private isGetBackupContentError(errorResponse: any): boolean {
    return errorResponse.method === 'GetBackupContent' && errorResponse.error.includes("Can't find part of the path: ");
  }

  private isGetComputerContentError(errorResponse: any): boolean {
    return errorResponse.method === 'GetComputerContent';
  }

  private isRemoteCommandError(error: HttpErrorResponse): boolean {
    return error.status === 400 && this.errorService.isRemoteCommand(error);
  }
}
